// Admin allowlist (by email). Used to gate dev-only affordances that should never
// be visible to the public, e.g. the solo warm-up entry that drops you straight
// into a private online match against fill-in opponents. Add emails as needed.
// The list itself lives in core (shared with the edge-server's fake-grant gate).
import { isAdminEmail } from "core/src/shop/admins.js";

// True when the signed-in user is an admin. Safe with null/undefined (logged out).
// On localhost (dev), everyone is an admin - no sign-in needed to reach the dev
// affordances (start-now, the render-delay slider, the shop test modes). Only
// client-side chrome is gated this way; anything server-enforced (fake grants)
// still re-checks the allowlist on the server.
export function isAdmin(user: { email?: string | null } | null | undefined): boolean {
  try {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return true;
  } catch { /* no window (tests) */ }
  return isAdminEmail(user?.email);
}

// Shop test mode (admin-only): purchases skip payment and grant instantly via
// the server's fake-grant route. Toggled on the Admin page, persisted here so
// the shop itself stays free of admin chrome. The server re-checks the admin
// allowlist on every grant, so this flag is inert for anyone else.
const FAKE_SHOP_KEY = "ar.fakeShop";
export function fakeShopEnabled(): boolean {
  try {
    return localStorage.getItem(FAKE_SHOP_KEY) === "1";
  } catch {
    return false;
  }
}
export function setFakeShopEnabled(on: boolean): void {
  try {
    if (on) localStorage.setItem(FAKE_SHOP_KEY, "1");
    else localStorage.removeItem(FAKE_SHOP_KEY);
  } catch {
    /* ignore */
  }
}

// Free-equip mode (admin-only): every cosmetic tile also offers "Equip" even
// when the item isn't owned, so heads / arenas / flags / celebrations can be
// tried without buying (equipping is client-side state, nothing is granted).
// Independent of the fake-shop toggle: use either, or both.
const FREE_EQUIP_KEY = "ar.freeEquip";
export function freeEquipEnabled(): boolean {
  try {
    return localStorage.getItem(FREE_EQUIP_KEY) === "1";
  } catch {
    return false;
  }
}
export function setFreeEquipEnabled(on: boolean): void {
  try {
    if (on) localStorage.setItem(FREE_EQUIP_KEY, "1");
    else {
      localStorage.removeItem(FREE_EQUIP_KEY);
      localStorage.removeItem(FREE_EQUIP_OWNED_KEY);
    }
  } catch {
    /* ignore */
  }
}
// Items "bought" while free-equip mode is on. They read as owned in the shop
// (so the cards look exactly like a real purchase) but nothing is granted
// server-side; the list is wiped when the mode is switched off.
const FREE_EQUIP_OWNED_KEY = "ar.freeEquipOwned";
export function freeEquipOwned(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(FREE_EQUIP_OWNED_KEY) || "[]"));
  } catch {
    return new Set();
  }
}
export function removeFreeEquipOwned(id: string): Set<string> {
  const next = freeEquipOwned();
  next.delete(id);
  try {
    localStorage.setItem(FREE_EQUIP_OWNED_KEY, JSON.stringify([...next]));
  } catch {
    /* ignore */
  }
  return next;
}
export function addFreeEquipOwned(id: string): Set<string> {
  const next = freeEquipOwned();
  next.add(id);
  try {
    localStorage.setItem(FREE_EQUIP_OWNED_KEY, JSON.stringify([...next]));
  } catch {
    /* ignore */
  }
  return next;
}

// Leaderboard "real players only" (admin-only): the public leaderboard lists
// the bots as ordinary players, so this switch on the leaderboard page hides
// them for an admin who wants to see how the humans are doing. Remembered
// here so the switch stays as you left it. The page only honours it for
// admins (isAdmin); to everyone else the leaderboard is always the full one.
const LB_HUMANS_KEY = "ar.lbHumans";
export function leaderboardHumansOnly(): boolean {
  try {
    return localStorage.getItem(LB_HUMANS_KEY) === "1";
  } catch {
    return false;
  }
}
export function setLeaderboardHumansOnly(on: boolean): void {
  try {
    if (on) localStorage.setItem(LB_HUMANS_KEY, "1");
    else localStorage.removeItem(LB_HUMANS_KEY);
  } catch {
    /* ignore */
  }
}
